// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
// apps/client/src/components/charts/RunDistributionChart.tsx
// Run Distribution Chart - Stacked bar chart showing run distribution patterns over time

import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, defs } from 'recharts';
import { MonthlyRctiSummary } from '../../types';

interface RunDistributionChartProps {
  months: MonthlyRctiSummary[];
  year: number;
  yearType: 'calendar' | 'financial';
}

interface ChartDataPoint {
  month: string;
  run1: number;
  run2: number;
  run3: number;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const RunDistributionChart: React.FC<RunDistributionChartProps> = ({ months, year, yearType }) => {
  // Build lookup map from months data (key: 'YYYY-MM')
  const dataByMonth = new Map(months.map(m => [m.month, m]));

  // Generate all 12 month slots (calendar: Jan-Dec, financial: Jul-Jun)
  const chartData: ChartDataPoint[] = Array.from({ length: 12 }, (_, i) => {
    let monthIdx: number;
    let monthYear: number;
    if (yearType === 'financial') {
      // Financial year: Jul(6) Aug(7) ... Dec(11) Jan(0) Feb(1) ... Jun(5)
      monthIdx = (i + 6) % 12;
      monthYear = i < 6 ? year : year + 1;
    } else {
      monthIdx = i;
      monthYear = year;
    }
    const monthKey = `${monthYear}-${String(monthIdx + 1).padStart(2, '0')}`;
    const data = dataByMonth.get(monthKey);

    const runMap = new Map<number | null, number>();
    if (data) {
      data.runs.forEach((run) => runMap.set(run.runNumber, run.totalCents));
    }

    return {
      month: MONTH_NAMES[monthIdx],
      run1: (runMap.get(1) || 0) / 100,
      run2: (runMap.get(2) || 0) / 100,
      run3: (runMap.get(3) || 0) / 100,
    };
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, entry: any) => sum + entry.value, 0);

      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm">
              <span
                className="inline-block w-3 h-3 rounded-sm mr-2"
                style={{ background: entry.name === 'Run 1' ? 'linear-gradient(180deg, #34d399 0%, #059669 100%)' :
                                     entry.name === 'Run 2' ? 'linear-gradient(180deg, #60a5fa 0%, #2563eb 100%)' :
                                     'linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)' }}
              />
              {entry.name}: ${entry.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          ))}
          <div className="border-t border-gray-600 mt-2 pt-2">
            <p className="font-bold text-emerald-400">
              Total: ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg mt-6 relative overflow-hidden">
      <div className="p-6">
        <h3 className="font-bold text-lg mb-4 text-white relative z-10">Run Distribution Trend</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            {/* Gradient Definitions */}
            <defs>
              {/* Run 1 - Emerald/Teal gradient */}
              <linearGradient id="gradientRun1" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#34d399" stopOpacity={1} />
                <stop offset="100%" stopColor="#059669" stopOpacity={1} />
              </linearGradient>
              {/* Run 2 - Blue gradient */}
              <linearGradient id="gradientRun2" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#60a5fa" stopOpacity={1} />
                <stop offset="100%" stopColor="#2563eb" stopOpacity={1} />
              </linearGradient>
              {/* Run 3 - Amber/Yellow gradient */}
              <linearGradient id="gradientRun3" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fbbf24" stopOpacity={1} />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.4} />
            <XAxis
              dataKey="month"
              stroke="#9ca3af"
              fontSize={12}
              tickLine={false}
              axisLine={{ stroke: '#4b5563' }}
            />
            <YAxis
              stroke="#9ca3af"
              fontSize={12}
              tickLine={false}
              axisLine={{ stroke: '#4b5563' }}
              tickFormatter={(value: number) => {
                if (!Number.isFinite(value)) return '$0k';
                const k = Math.round(value / 1000);
                return `$${k}k`;
              }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="square"
              formatter={(value) => <span className="text-gray-300">{value}</span>}
            />
            <Bar
              dataKey="run1"
              name="Run 1"
              stackId="a"
              fill="url(#gradientRun1)"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="run2"
              name="Run 2"
              stackId="a"
              fill="url(#gradientRun2)"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="run3"
              name="Run 3"
              stackId="a"
              fill="url(#gradientRun3)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RunDistributionChart;
